/**
 * Tax summary — `GET reports/tax-summary/?year=YYYY`, manager roles only.
 * Node-only, no Django counterpart (compliance item: KRA/tax tracking — see
 * refactor/backend/src/modules/reports/routes.ts's final section). NOT a tax
 * calculation: an aggregation of rent collected minus expenses, per
 * property, for the landlord/agent's own accountant to work from when
 * filing rental income tax with KRA.
 */
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { parseApiError } from '../../lib/apiError';
import { getTaxSummary } from '../../api/endpoints/reports';

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2, CURRENT_YEAR - 3];

export function TaxSummaryPage(): React.JSX.Element {
  const [year, setYear] = useState(CURRENT_YEAR);

  const query = useQuery({
    queryKey: ['reports-tax-summary', { year }],
    queryFn: () => getTaxSummary(year),
  });

  /** Client-side PDF, same shared chrome module the other 3 report exports use — no server-side PDF endpoint. */
  const exportPdf = useMutation({
    mutationFn: async () => {
      const report = query.data;
      if (report === undefined) return;

      const { jsPDF } = await import('jspdf');
      const { createBrandedDocument, drawTable, downloadPdf, formatPdfMoney, PAGE_MARGIN } =
        await import('../../lib/pdf');

      const { doc, contentStartY } = createBrandedDocument(jsPDF, `TAX SUMMARY — ${report.year}`);
      let y = contentStartY;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.text(`KRA PIN: ${report.kra_pin ?? 'Not set — see Profile'}`, PAGE_MARGIN, y);
      y += 6;
      doc.text(`Total gross rental income: ${formatPdfMoney(report.total_gross_rental_income)}`, PAGE_MARGIN, y);
      y += 6;
      doc.text(`Total deductible expenses: ${formatPdfMoney(report.total_deductible_expenses)}`, PAGE_MARGIN, y);
      y += 6;
      doc.text(`Total net income: ${formatPdfMoney(report.total_net_income)}`, PAGE_MARGIN, y);
      y += 10;

      drawTable(doc, {
        startY: y,
        head: ['Property', 'Gross income', 'Expenses', 'Net income'],
        rows: report.by_property.map((row) => [
          row.property_name,
          formatPdfMoney(row.gross_rental_income),
          formatPdfMoney(row.deductible_expenses),
          formatPdfMoney(row.net_income),
        ]),
      });

      downloadPdf(doc, `tax-summary-${report.year}.pdf`);
    },
  });

  return (
    <section>
      <h2>Tax summary</h2>
      <p>
        A per-property breakdown of rent collected minus expenses, for your own accountant's use
        when filing rental income tax with KRA. This is not a tax calculation.
      </p>

      <label htmlFor="tax-summary-year">Year</label>
      <select id="tax-summary-year" value={year} onChange={(event) => setYear(Number(event.target.value))}>
        {YEAR_OPTIONS.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>

      {query.isPending && <p>Loading…</p>}
      {query.isError && (
        <p role="alert" className="form__error">
          {parseApiError(query.error).message}
        </p>
      )}

      {query.isSuccess && (
        <>
          <p>
            <button type="button" disabled={exportPdf.isPending} onClick={() => exportPdf.mutate()}>
              {exportPdf.isPending ? 'Exporting…' : 'Export PDF'}
            </button>
          </p>

          {exportPdf.isError && (
            <p role="alert" className="form__error">
              {parseApiError(exportPdf.error).message}
            </p>
          )}

          <dl>
            <dt>KRA PIN</dt>
            <dd>
              {query.data.kra_pin ?? (
                <>
                  Not set — <a href="../profile">add it on your Profile</a>.
                </>
              )}
            </dd>
            <dt>Total gross rental income</dt>
            <dd>{query.data.total_gross_rental_income}</dd>
            <dt>Total deductible expenses</dt>
            <dd>{query.data.total_deductible_expenses}</dd>
            <dt>Total net income</dt>
            <dd>{query.data.total_net_income}</dd>
          </dl>

          <h3>By property</h3>
          {query.data.by_property.length === 0 ? (
            <p>No rental income recorded for {year}.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Gross income</th>
                  <th>Expenses</th>
                  <th>Net income</th>
                </tr>
              </thead>
              <tbody>
                {query.data.by_property.map((row) => (
                  <tr key={row.property_id}>
                    <td>{row.property_name}</td>
                    <td>{row.gross_rental_income}</td>
                    <td>{row.deductible_expenses}</td>
                    <td>{row.net_income}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}

export default TaxSummaryPage;
